import { useQuery } from "@tanstack/react-query";
import { unwrap } from "@brad-tipper/app-frontend";
import { api } from "~/api/client";
import type { Recipe as ApiRecipe } from "~/api/generated/types.gen";
import { backendConfigured } from "~/lib/constants";
import { parseRecipeSteps } from "~/lib/parse-recipe-steps";
import type { Recipe } from "~/types/models";

export { parseRecipeSteps };
export type { Recipe, RecipeStep } from "~/types/models";

function mapRecipe(row: ApiRecipe): Recipe {
  return {
    id: row.id,
    title: row.title,
    description: row.description,
    category: row.category,
    difficulty: row.difficulty,
    prepTimeMinutes: row.prep_time_minutes,
    fermentTimeHours: row.ferment_time_hours,
    servings: row.servings,
    ingredients: row.ingredients,
    instructions: row.instructions,
    steps: row.steps,
    emoji: row.emoji,
  };
}

// The catalogue is public (no session needed), so guests can browse recipes
// before ever creating an account.
async function fetchRecipes(): Promise<Recipe[]> {
  const list = await unwrap(api.listRecipes());
  return list.items.map(mapRecipe);
}

export function useRecipesFiltered(filters?: {
  query?: string;
  difficulty?: string;
}) {
  const search = filters?.query?.trim() ?? "";
  const difficulty = filters?.difficulty ?? "";

  const query = useQuery({
    queryKey: ["recipes"],
    enabled: backendConfigured,
    queryFn: fetchRecipes,
  });

  const needle = search.toLowerCase();
  const data = (query.data ?? []).filter(
    (recipe) =>
      (!difficulty || recipe.difficulty === difficulty) &&
      (!needle ||
        recipe.title.toLowerCase().includes(needle) ||
        recipe.description.toLowerCase().includes(needle) ||
        recipe.ingredients.toLowerCase().includes(needle)),
  );

  return {
    data,
    loading: query.isPending,
    error: query.error ? query.error.message : null,
    refetch: query.refetch,
  };
}

export function useRecipes() {
  return useRecipesFiltered();
}

export function useRecipe(id: string | undefined) {
  const query = useQuery({
    queryKey: ["recipe", id],
    enabled: Boolean(backendConfigured && id),
    queryFn: async () => {
      if (!id) return null;
      return mapRecipe(await unwrap(api.getRecipe({ path: { id } })));
    },
  });

  return {
    data: query.data ?? null,
    loading: query.isPending,
    error: query.error ? query.error.message : null,
  };
}
